<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.user.role.callAgent', 2) },
      { label: $t('ui.entities.statusSummary') }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-3">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a ${$t('ui.entities.departments.callCentre')}`"
          class="max-w-[250px]"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>

        <DatePicker
          v-model="dateSelected"
          mode="single"
          display-as="button"
          display-format="full"
          :max-date="new Date()"
        />
        <div>
          <Button
            label="Update"
            :is-loading="isLoading"
            @click="onFetchData"
          />
        </div>
      </div>
    </template>
  </PageHeader>

  <Card
    v-if="data.length"
    :is-loading="apiRequest.isLoading.value"
  >
    <div
      v-for="user in data"
      :key="user.id"
      class="table-record grid grid-cols-6"
    >
      <div class="table-record-cell">
        <div class="flex items-center">
          <Avatar
            size="sm"
            :url="$cdnBaseUrl + '/img/profile-pics/' + user.profilePicUrl"
            :text="user.initials"
            :user-presence-user-id="user.id"
          />
          <span class="ml-3">{{ user.forename }} {{ user.surname }}</span>
        </div>
      </div>

      <div class="col-span-5 mt-4">
        <ActivityTimeline
          :data="user"
          mode="summary"
        />
      </div>
    </div>
  </Card>
  <Card
    v-else-if="!isLoading"
    class="h-[70vh] flex items-center justify-center"
  >
    <Empty :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCentre')}`" />
  </Card>
  <div
    v-else
    class="flex gap-4"
  >
    <Skeleton
      width="18rem"
      height="36rem"
    />
    <Skeleton
      width="100%"
      height="36rem"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import Button from '@/components/button/Button.vue'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'
import useApiRequest from '@/composables/useApiRequest'

import dayjs from 'dayjs'

import ActivityTimeline from '@/components/user-activity/UserActivityTimeline.vue'

const dateSelected = ref(dayjs().format('YYYY-MM-DD'))
const data = ref([])
const apiRequest = useApiRequest()
const isLoading = apiRequest.isLoading
const callCenterStore = useCallCenterStore()

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

function getData() {
  apiRequest
    .send({
      endpoint: '/v1/users/activity/timelines',
      params: {
        callCenterId: callCenterSelected.value,
        date: dayjs(dateSelected.value).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      if (response.success) {
        data.value = response.data
      }
    })
}

if (!localStorage.getItem('report-filter-call-center-selected')) {
  callCenterSelected.value = callCentersAllowed[0].id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

getData()

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
}

function onFetchData() {
  if (dateSelected.value && callCenterSelected.value) {
    getData()
  }
}
</script>

<style>
.table-record {
  border-bottom: 1px solid var(--border-secondary);
  overflow: hidden;
}

.table-record > .table-record-cell {
  padding: 1rem;
}
</style>
